import {DispatchEntity, dispatchStatus} from "@ots-frontend/ots-data";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Menu} from "@mantine/core";
import {
  IconBell,
  IconMessagePlus,
  IconCircleX,
  IconCopy,
  IconEdit,
  IconEye,
  IconFileBarcode,
  IconLink,
  IconTrash,
  IconUserEdit,
} from "@tabler/icons-react";
import {HasAccess} from "../../../auth/HasAccess/HasAccess";

export type DispatchDataGridContextMenuItem =
  | "NEW"
  | "DELETE"
  | "EDIT"
  | "CANCEL"
  | "ASSIGN"
  | "NOTIFY"
  | "CLEAR"
  | "COMMENT";

export type DispatchDataGridContextMenuProps = {
  data: DispatchEntity[];
  onItemClick: (item: DispatchDataGridContextMenuItem, data: DispatchEntity[]) => void;
};

export const DispatchDataGridContextMenu = (props: DispatchDataGridContextMenuProps) => {
  const {data, onItemClick} = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  if (data.length > 1) {
    return (
      <HasAccess permissions={["dispatch_edit"]}>
        <Menu shadow="md" width={200} opened={true}>
          <Menu.Dropdown>
            <Menu.Item icon={<IconUserEdit size={14} />} onClick={() => onItemClick("ASSIGN", data)}>
              {t("common.action.assign.driver", "Assign Driver")}
            </Menu.Item>
            <Menu.Item icon={<IconBell size={14} />} onClick={() => onItemClick("NOTIFY", data)}>
              {t("common.action.notify", "Notify")}
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item onClick={() => onItemClick("CLEAR", data)}>{t("common.action.clear", "Clear")}</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </HasAccess>
    );
  }

  const item = data[0];

  return (
    <Menu shadow="md" width={200} opened={true}>
      <Menu.Dropdown>
        <Menu.Item icon={<IconEye size={14} />} onClick={() => navigate(`/dispatch/profile?id=${item.id}`)}>
          {t("common.action.view", "View")}
        </Menu.Item>
        <Menu.Item icon={<IconBell size={14} />} onClick={() => onItemClick("NOTIFY", [item])}>
          {t("common.action.notify", "Notify")}
        </Menu.Item>
        {item.dispatchRequest && (
          <HasAccess permissions={["dispatchrequest_view"]}>
            <Menu.Item
              icon={<IconLink size={14} />}
              onClick={() => navigate(`/requests/profile?id=${item.dispatchRequest.id}`)}
            >
              {t("common.action.view.dispatchrequest", "View Dispatch Request")}
            </Menu.Item>
          </HasAccess>
        )}
        <HasAccess permissions={["dispatch_edit"]}>
          <>
            <Menu.Item icon={<IconMessagePlus size={14} />} onClick={() => onItemClick("COMMENT", [item])}>
              {t("common.action.add.comment", "Add comment")}
            </Menu.Item>
            <Menu.Item icon={<IconUserEdit size={14} />} onClick={() => onItemClick("ASSIGN", [item])}>
              {typeof item.personId === "undefined"
                ? t("common.action.assign.driver", "Assign Driver")
                : t("common.action.reassign.driver", "Reassign Driver")}
            </Menu.Item>
            <Menu.Item icon={<IconEdit size={14} />} onClick={() => onItemClick("EDIT", [item])}>
              {t("common.action.edit", "Edit")}
            </Menu.Item>
            {item.status.code !== dispatchStatus.canceled.code && (
              <Menu.Item icon={<IconCircleX size={14} />} color="red" onClick={() => onItemClick("CANCEL", [item])}>
                {t("common.action.cancel", "Cancel")}
              </Menu.Item>
            )}
          </>
        </HasAccess>
        <HasAccess permissions={["dispatch_delete"]}>
          <>
            <Menu.Item icon={<IconTrash size={14} />} color="red" onClick={() => onItemClick("DELETE", [item])}>
              {t("common.action.delete", "Delete")}
            </Menu.Item>
          </>
        </HasAccess>
        <HasAccess permissions={["dispatch_create"]}>
          <>
            <Menu.Item icon={<IconCopy size={14} />} onClick={() => onItemClick("NEW", [item])}>
              {t("common.action.new", "New")}
            </Menu.Item>
          </>
        </HasAccess>
        {item.pickupTransportId && item.deliveryTransportId && (
          <HasAccess permissions={["transporttransportassoc_view"]}>
            <>
              <Menu.Divider />
              <Menu.Item
                icon={<IconFileBarcode size={14} />}
                onClick={() =>
                  navigate(`/tickets/profile?pid=${item.pickupTransportId}&did=${item.deliveryTransportId}`)
                }
              >
                {t("common.action.view", "View") + " " + t("common.entity.transporttransportassoc", "Ticket")}
              </Menu.Item>
            </>
          </HasAccess>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
